import type { PresenceStateData } from "@productlane/lib"
import { PRESENCE_HEARTBEAT_MS } from "@productlane/lib"

import { acquireWorkspaceSocket } from "@/lib/sockets/workspace-socket"

interface PresenceSocketOptions {
	workspaceId: string
	userId: string
	onState: (data: PresenceStateData) => void
}

export interface PresenceSocket {
	close: () => void
}

function resolveTimeZone(): string | undefined {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined
	} catch {
		return undefined
	}
}

/**
 * Keeps this tab's member marked online and mirrors the workspace's online set.
 */
export function createPresenceSocket({
	workspaceId,
	userId,
	onState,
}: PresenceSocketOptions): PresenceSocket {
	const { socket, release } = acquireWorkspaceSocket()

	const payload = {
		workspaceId,
		userId,
		timeZone: resolveTimeZone(),
	}

	const handleConnect = () => {
		socket.emit("presence:subscribe", payload)
	}

	const handleState = (data: PresenceStateData) => {
		onState(data)
	}

	socket.on("connect", handleConnect)
	socket.on("presence:state", handleState)

	if (socket.connected) handleConnect()

	// A hidden tab stops beating, so a laptop left open overnight drops off.
	const heartbeat = setInterval(() => {
		if (document.visibilityState !== "visible") return
		socket.emit("presence:heartbeat", payload)
	}, PRESENCE_HEARTBEAT_MS)

	const handleVisibility = () => {
		if (document.visibilityState !== "visible") return
		socket.emit("presence:heartbeat", payload)
	}
	document.addEventListener("visibilitychange", handleVisibility)

	return {
		close: () => {
			clearInterval(heartbeat)
			document.removeEventListener("visibilitychange", handleVisibility)
			socket.off("connect", handleConnect)
			socket.off("presence:state", handleState)
			release()
		},
	}
}
